import { formatAmount, getPreselectedQuantities } from "./selection";
import type {
  PersonalizedAddonsSeason,
  SuggestedAddon,
  SuggestedAddonVariant,
} from "./types";

type Deficiency = NonNullable<
  SuggestedAddon["connectedIssues"]
>[number]["deficiency"];
type Severity = NonNullable<
  NonNullable<SuggestedAddon["connectedIssues"]>[number]["severity"]
>;
type AddonKind =
  | "WEED_EX"
  | "MOSS_FREE"
  | "OVERSEED"
  | "LAWN_PATCH"
  | "NEW_SEED"
  | "WETTING_AGENT"
  | "SOIL_IMPROVER"
  | "OTHER";

const FALLBACK_TAG_TEXT: Record<Deficiency, string> = {
  WEEDS: "Unkraut",
  MOSS: "Moos",
  BARE_SPOTS: "kahle Stellen",
  LOW_DENSITY: "dünner Rasen",
  HEAT_STRESS: "Trockenstress",
  URINE_SPOTS: "Urinflecken",
};

const hasIssue = (addon: SuggestedAddon, deficiency: Deficiency) =>
  addon.connectedIssues?.some((issue) => issue.deficiency === deficiency) ??
  false;

const getIssueSeverity = (
  addon: SuggestedAddon,
  deficiency: Deficiency,
): Severity | undefined =>
  addon.connectedIssues?.find((issue) => issue.deficiency === deficiency)
    ?.severity;

const includesSlugOrTitle = (
  addon: SuggestedAddon,
  slugParts: string[],
  titlePattern: RegExp,
) => {
  const slug = addon.slug?.toLowerCase() ?? "";
  return (
    slugParts.some((part) => slug.includes(part)) ||
    titlePattern.test(addon.title)
  );
};

const getAddonKind = (addon: SuggestedAddon): AddonKind => {
  if (
    includesSlugOrTitle(addon, ["weedex", "unkrautfrei"], /weedex|unkraut/i)
  ) {
    return "WEED_EX";
  }
  if (includesSlugOrTitle(addon, ["moosfrei"], /moosfrei/i)) {
    return "MOSS_FREE";
  }
  if (includesSlugOrTitle(addon, ["rasenpflaster"], /rasenpflaster/i)) {
    return "LAWN_PATCH";
  }
  if (includesSlugOrTitle(addon, ["kraftsaat"], /kraftsaat|neusaat/i)) {
    return "NEW_SEED";
  }
  if (includesSlugOrTitle(addon, ["wetting-agent"], /wetting/i)) {
    return "WETTING_AGENT";
  }
  if (
    includesSlugOrTitle(
      addon,
      ["bodenkraft", "bodenverbesserer"],
      /bodenkraft|bodenverbesserer/i,
    )
  ) {
    return "SOIL_IMPROVER";
  }
  if (
    includesSlugOrTitle(
      addon,
      ["fullsaat", "füllsaat", "allwetter"],
      /fullsaat|füllsaat|nachsaat|allwetter/i,
    )
  ) {
    return "OVERSEED";
  }
  return "OTHER";
};

const joinTagReasons = (reasons: string[]) =>
  reasons.length ? `Hilft gegen: ${Array.from(new Set(reasons)).join(" & ")}` : null;

export const getRecommendationReasonTag = (
  addon: SuggestedAddon,
  season: PersonalizedAddonsSeason,
) => {
  const kind = getAddonKind(addon);

  if (kind === "SOIL_IMPROVER") return null;
  if (kind === "WEED_EX")
    return hasIssue(addon, "WEEDS") ? "Hilft gegen: Unkraut" : null;
  if (kind === "MOSS_FREE")
    return hasIssue(addon, "MOSS") ? "Hilft gegen: Moos" : null;

  if (kind === "OVERSEED") {
    if (hasIssue(addon, "LOW_DENSITY")) return "Hilft gegen: dünner Rasen";

    const hasWeeds = hasIssue(addon, "WEEDS");
    const hasMoss = hasIssue(addon, "MOSS");
    if (hasWeeds && hasMoss)
      return "Hilft gegen: Lücken nach Unkraut & Moosentfernung";
    if (hasWeeds) return "Hilft gegen: Lücken nach Unkrautentfernung";
    if (hasMoss) return "Hilft gegen: Lücken nach Moosentfernung";
  }

  if (kind === "LAWN_PATCH") {
    return joinTagReasons([
      ...(hasIssue(addon, "BARE_SPOTS") ? ["kleine kahle Stellen"] : []),
      ...(hasIssue(addon, "URINE_SPOTS") ? ["Urinflecken"] : []),
    ]);
  }

  if (kind === "NEW_SEED" && hasIssue(addon, "BARE_SPOTS")) {
    return "Hilft gegen: große kahle Stellen";
  }

  if (kind === "WETTING_AGENT") {
    if (season === "summer") return "Hilft gegen: Sommerhitze";
    if (hasIssue(addon, "HEAT_STRESS")) return "Hilft gegen: Trockenstress";
  }

  return joinTagReasons(
    addon.connectedIssues?.map(
      (issue) => FALLBACK_TAG_TEXT[issue.deficiency],
    ) ?? [],
  );
};

const getSelectedVariants = (addon: SuggestedAddon) => {
  const quantities = getPreselectedQuantities(addon);
  const variants = addon.variants.filter(
    (variant) => (quantities[variant.variantId] ?? 0) > 0,
  );
  return { quantities, variants };
};

const getDisplayUnit = (variant: SuggestedAddonVariant) =>
  ({ Kg: "kg", ml: "ml", g: "g" })[variant.weight.unit];

const formatCoverage = (coverage: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(coverage);

export const getRecommendationLabel = (
  addon: SuggestedAddon,
  lawnSize: number | null,
) => {
  const { quantities, variants } = getSelectedVariants(addon);
  if (!variants.length) return null;

  const amount = variants
    .map(
      (variant) =>
        `${quantities[variant.variantId]} × ${formatAmount(
          variant.weight.amount,
        )} ${getDisplayUnit(variant)}`,
    )
    .join(" + ");
  const coverage = variants.reduce(
    (total, variant) =>
      total + variant.lawnSizeCovered * quantities[variant.variantId],
    0,
  );
  const coversWholeLawn = lawnSize !== null && coverage >= lawnSize;
  const coverageText = `${formatCoverage(coverage)} m²`;
  const kind = getAddonKind(addon);

  if (kind === "WEED_EX") {
    const context = {
      SPOTS: "Unkraut an einzelnen Stellen",
      WIDESPREAD: "verbreitetem Unkraut",
      FULL: "weit verbreitetem Unkraut",
    }[getIssueSeverity(addon, "WEEDS") ?? "SPOTS"];
    return coversWholeLawn
      ? `Empfohlen bei ${context}: ${amount} – reicht für deinen ganzen Rasen`
      : `Empfohlen bei ${context}: ${amount} für ${coverageText}`;
  }

  if (kind === "MOSS_FREE") {
    const context = {
      SPOTS: "Moos an einzelnen Stellen",
      WIDESPREAD: "verbreitetem Moos",
      FULL: "weit verbreitetem Moos",
    }[getIssueSeverity(addon, "MOSS") ?? "SPOTS"];
    return coversWholeLawn
      ? `Empfohlen bei ${context}: ${amount} – reicht für deinen ganzen Rasen`
      : `Empfohlen bei ${context}: ${amount} für ${coverageText}`;
  }

  if (kind === "OVERSEED") {
    let context = "zum Lückenschließen";
    if (hasIssue(addon, "LOW_DENSITY")) context = "zum Verdichten";
    else if (hasIssue(addon, "WEEDS") && !hasIssue(addon, "MOSS")) {
      context = "zum Lückenschließen nach Unkraut";
    } else if (hasIssue(addon, "MOSS") && !hasIssue(addon, "WEEDS")) {
      context = "zum Lückenschließen nach Moos";
    }
    return coversWholeLawn
      ? `Empfohlen ${context}: ${amount} – reicht für deinen ganzen Rasen`
      : `Empfohlen ${context}: ${amount} für ${coverageText}`;
  }

  if (kind === "LAWN_PATCH") {
    const area = hasIssue(addon, "URINE_SPOTS")
      ? `${coverageText} Flecken`
      : `${coverageText} kahle Stellen`;
    return `Empfohlen: ${amount} – reicht für ca. ${area}`;
  }

  if (kind === "NEW_SEED") {
    return coversWholeLawn
      ? `Empfohlen für große kahle Stellen: ${amount} – reicht für deinen ganzen Rasen`
      : `Empfohlen für große kahle Stellen: ${amount} für ${coverageText}`;
  }

  if (kind === "WETTING_AGENT") {
    return coversWholeLawn
      ? `Empfohlen für deinen ganzen Rasen: ${amount}`
      : `Empfohlen für deinen Rasen: ${amount} für ${coverageText}`;
  }

  if (kind === "SOIL_IMPROVER") {
    return coversWholeLawn
      ? `${amount} – reicht für deinen ganzen Rasen`
      : `${amount} für ${coverageText}`;
  }

  return coversWholeLawn
    ? `Empfohlen: ${amount} – reicht für deinen ganzen Rasen`
    : `Empfohlen: ${amount} für ${coverageText}`;
};
